import { PropsWithChildren, RefObject, useEffect, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import { rgba } from 'polished';
import { Quill } from 'quill';

import { BoldIcon, ItalicIcon, LinkIcon, ListIcon, UnderlineIcon } from '@treams/treams-icons';

import { Popover } from '../Popover';
import QuillLinkProperties from './QuillLinkProperties';
import { LanguageSelect } from '../LanguageSelect';

const Container = styled(Flex)`
	background: ${(props) => props.theme.colors.lightBlueLight};
	border-bottom: 1px solid ${(props) => props.theme.colors.lightBlue};
`;

const ToolbarButtons = styled(Flex)`
	border-radius: 4px;
	background: white;
	box-shadow: 0px 1px 2px ${(props) => rgba(props.theme.colors.slateBlue, 0.16)};
	overflow: hidden;
`;

const ToolbarButtonContainer = styled(Box)<{ active?: boolean }>`
	padding: 8px 16px;
	flex-grow: 0;
	flex-shrink: 0;
	cursor: pointer;

	svg {
		display: block;
		width: 16px;
		height: 16px;
	}

	&:not(:first-child) {
		border-left: 1px solid
			${(props) => (props.active ? props.theme.colors.primary : props.theme.colors.lightBlue)};
	}

	&:hover {
		background: ${(props) =>
			props.active ? props.theme.colors.primary : props.theme.colors.whiteBlueLight};
	}

	${(props) =>
		props.active &&
		`background: ${props.theme.colors.primary};
		margin-right: -1px;
		padding-right: 17px;
		
		svg {
			path {
				fill: white;
			}
		}`}
`;

function useRerender() {
	const [, setValue] = useState(false);

	return () => setValue((value) => !value);
}

function ToolbarButton<T>(
	props: PropsWithChildren<{
		innerRef: RefObject<Quill | undefined>;
		name: string;
		value?: (initialValue?: T) => Promise<T | undefined>;
	}>,
) {
	const rerender = useRerender();
	const initialValue = props.innerRef.current?.getSelection()
		? props.innerRef.current?.getFormat()[props.name]
		: undefined;

	function onSelectionChange() {
		rerender();
	}

	useEffect(() => {
		props.innerRef.current?.on('selection-change', onSelectionChange);

		return () => {
			props.innerRef.current?.off('selection-change', onSelectionChange);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<ToolbarButtonContainer
			active={!!initialValue}
			onMouseDown={(event) => event.preventDefault()}
			onClick={async () => {
				const value = props.value ? await props.value?.(initialValue) : !initialValue;
				props.innerRef.current?.focus();
				props.innerRef.current?.format(props.name, value, 'user');
				rerender();
			}}
		>
			{props.children}
		</ToolbarButtonContainer>
	);
}

export function QuillToolbar(props: {
	innerRef: RefObject<Quill | undefined>;
	localizable?: boolean;
}) {
	const [link, setLink] = useState<
		{ initialValue?: string; resolve: (value?: string) => void } | undefined
	>();

	return (
		<Container p={1} alignItems="center">
			<ToolbarButtons>
				<ToolbarButton innerRef={props.innerRef} name="bold">
					<BoldIcon />
				</ToolbarButton>

				<ToolbarButton innerRef={props.innerRef} name="italic">
					<ItalicIcon />
				</ToolbarButton>

				<ToolbarButton innerRef={props.innerRef} name="underline">
					<UnderlineIcon />
				</ToolbarButton>
			</ToolbarButtons>

			<ToolbarButtons ml={2}>
				<ToolbarButton innerRef={props.innerRef} name="list">
					<ListIcon />
				</ToolbarButton>
			</ToolbarButtons>

			<ToolbarButtons ml={2}>
				<Popover.Target
					contents={
						<QuillLinkProperties
							initialValue={link?.initialValue}
							onDismiss={(value) => {
								props.innerRef.current?.focus();

								if (value === '') {
									link?.resolve(undefined);
								} else if (value) {
									link?.resolve(value);
								}

								setLink(undefined);
							}}
						/>
					}
					visible={!!link}
					onDismiss={() => {
						setLink(undefined);
						props.innerRef.current?.focus();
					}}
				>
					<ToolbarButton
						innerRef={props.innerRef}
						name="link"
						value={(initialValue?: string) =>
							new Promise<string | undefined>((resolve) => {
								setLink({ initialValue, resolve });
							})
						}
					>
						<LinkIcon />
					</ToolbarButton>
				</Popover.Target>
			</ToolbarButtons>

			{props.localizable && (
				<>
					<Box flexGrow={1} />

					<ToolbarButtons ml={2}>
						<LanguageSelect.Scope />
					</ToolbarButtons>
				</>
			)}
		</Container>
	);
}
